import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText, Badge, Alert } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AgentPaymentCreate = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [invoiceList, setInvoiceList] = useState([]);
    const [profitInfo, setProfitInfo] = useState({
        total_payable: 0,
        already_paid: 0,
        balance: 0,
        rate_diff: 0,
        invoice_no: "",
        client_name: "",
        lorry_no: "",
        total_kg: 0
    });

    const [formData, setFormData] = useState({
        invoice_id: invoiceId || "",
        agent_name: "",
        agent_phone: "",
        payment_type: "purchase",
        amount: "",
        note: ""
    });

    // Error state for separate field validation
    const [errors, setErrors] = useState({});

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#2e4a3d' : (errors.invoice_id ? '#dc3545' : '#dee2e6'),
            boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(46, 74, 61, 0.25)' : 'none',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                const res = await Handler({ method: "GET", url: "/invoice/list.php" });
                if (res?.success) {
                    const options = res.data.map(item => ({
                        value: String(item.invoice.id),
                        label: `${item.invoice.invoice_no} - ${item.client.name}`
                    }));
                    setInvoiceList(options);
                    if (invoiceId) fetchProfitDetails(invoiceId);
                    dispatch(loadingFalse());
                }
            } catch (err) { console.error("List Load Error"); }
        };
        fetchInvoices();
    }, [invoiceId]);

    const fetchProfitDetails = async (id) => {
        if (!id || id === "undefined") return;
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: `/invoice/view.php?id=${id}` });
            if (res?.success && res.data) {
                const { agent_payments, invoice, client, lorry, summary } = res.data;

                let finalPayable = Number(agent_payments?.total_payable_amount || 0);
                let finalPaid = Number(agent_payments?.paid_amount || 0);
                let finalBalance = Number(agent_payments?.balance_amount || 0);

                const isAgentDataEmpty = finalPayable === 0 && finalPaid === 0 && finalBalance === 0;

                if (isAgentDataEmpty) {
                    const profit = Number(summary?.profit || 0);
                    if (profit < 0) {
                        finalPayable = Math.abs(profit); 
                        finalBalance = Math.abs(profit);
                    }
                }

                const rateDiff = Number(invoice?.rate_per_kg || 0) - Number(invoice?.actual_rate_per_kg || 0);
                
                setProfitInfo({
                    total_payable: finalPayable,
                    already_paid: finalPaid,
                    balance: finalBalance,
                    rate_diff: rateDiff,
                    invoice_no: invoice?.invoice_no,
                    client_name: client?.name,
                    lorry_no: lorry?.lorry_number,
                    total_kg: summary?.total_kg || 0
                });

                if (agent_payments?.entries?.length > 0) {
                    const lastEntry = agent_payments.entries[0];
                    setFormData(prev => ({
                        ...prev,
                        agent_name: lastEntry.agent.name,
                        agent_phone: lastEntry.agent.phone
                    }));
                }
                dispatch(loadingFalse());
            }
        } catch (err) {
            console.error("View API Error", err);
        } finally {
            dispatch(loadingFalse());
        }
    };

    // 🔥 Strict English Validation Logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.invoice_id) {
            formErrors.invoice_id = "Please select a target invoice";
            isValid = false;
        }

        if (!formData.agent_name.trim()) {
            formErrors.agent_name = "Agent name is required";
            isValid = false;
        }

        if (!formData.agent_phone || formData.agent_phone.length !== 10) {
            formErrors.agent_phone = "Mobile number must be exactly 10 digits";
            isValid = false;
        }

        if (!formData.amount || Number(formData.amount) <= 0) {
            formErrors.amount = "Please enter a valid payment amount";
            isValid = false;
        } else if (Number(formData.amount) > Number(profitInfo.balance)) {
            formErrors.amount = `Amount exceeds available balance (Max: ₹${profitInfo.balance})`;
            isValid = false;
        }

        setErrors(formErrors);
        return isValid;
    };

    const handleInvoiceChange = (opt) => {
        const selectedId = opt ? opt.value : "";
        setFormData({ ...formData, invoice_id: selectedId, amount: "" });
        setErrors({ ...errors, invoice_id: "" });
        if (selectedId) fetchProfitDetails(selectedId);
        else setProfitInfo({ total_payable: 0, already_paid: 0, balance: 0, rate_diff: 0 });
    };

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    const referenceKg = formData.amount > 0 && profitInfo.rate_diff > 0
        ? (formData.amount / profitInfo.rate_diff).toFixed(2)
        : 0;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Trigger local validation
        if (!validateForm()) {
            toast.error("Please correct the errors in the form.");
            return;
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "POST", url: "/agents/create.php", data: formData });
            if (res?.success) {
                Swal.fire({
                    icon: "success",
                    title: "Saved!",
                    text: "Agent payment recorded successfully",
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate("/agent-pay");
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Failed to record payment");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-3 p-md-4 bg-light min-vh-100">
            <div className="page-header mb-4 d-flex justify-content-between align-items-center">
                <div>
                    <h3 className="fw-bold text-theme m-0">Agent Payment Collection</h3>
                    <p className="text-muted small m-0">Process agent commissions or loss settlements.</p>
                </div>
                <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                    <i className="bx bx-arrow-back me-1"></i> Back
                </Button>
            </div>

            <Form onSubmit={handleSubmit}>
                <Row className="g-4">
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4">
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4 border-start border-4 border-primary ps-3">Payment Details</h5>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="small fw-bold opacity-75">SELECT INVOICE *</Label>
                                            <Select
                                                options={invoiceList}
                                                styles={customSelectStyles}
                                                value={invoiceList.find(i => i.value == formData.invoice_id)}
                                                onChange={handleInvoiceChange}
                                                placeholder="Search Invoice Number..."
                                                isClearable
                                            />
                                            {errors.invoice_id && <small className="text-danger mt-1">{errors.invoice_id}</small>}
                                        </FormGroup>
                                    </Col>

                                    {formData.invoice_id && (
                                        <Col md={12}>
                                            <div className="p-3 rounded-3 bg-light border mb-2 d-flex justify-content-between flex-wrap gap-2 small shadow-sm animate__animated animate__fadeIn">
                                                <div><span className="text-muted d-block">Client:</span><span className="fw-bold text-theme">{profitInfo.client_name}</span></div>
                                                <div><span className="text-muted d-block">Lorry:</span><span className="fw-bold">{profitInfo.lorry_no}</span></div>
                                                <div><span className="text-muted d-block">Total Weight:</span><span className="fw-bold">{profitInfo.total_kg} KG</span></div>
                                                <div><span className="text-muted d-block">Rate Diff:</span><span className="fw-bold text-success">₹{profitInfo.rate_diff} /kg</span></div>
                                            </div>
                                        </Col>
                                    )}

                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="small fw-bold opacity-75">AGENT NAME *</Label>
                                            <Input 
                                                type="text" 
                                                className={`form-control-lg rounded-3 ${errors.agent_name ? 'is-invalid' : ''}`} 
                                                value={formData.agent_name} 
                                                onChange={(e) => {setFormData({ ...formData, agent_name: e.target.value }); setErrors({...errors, agent_name:""})}} 
                                            />
                                            {errors.agent_name && <small className="text-danger mt-1">{errors.agent_name}</small>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold opacity-75">MOBILE NUMBER (10 DIGITS) *</Label>
                                            <Input 
                                                type="number" 
                                                onKeyDown={blockInvalidChar} 
                                                className={`form-control-lg rounded-3 ${errors.agent_phone ? 'is-invalid' : ''}`} 
                                                value={formData.agent_phone} 
                                                onChange={(e) => {setFormData({ ...formData, agent_phone: e.target.value }); setErrors({...errors, agent_phone:""})}} 
                                            />
                                            {errors.agent_phone && <small className="text-danger mt-1">{errors.agent_phone}</small>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold opacity-75">ENTRY TYPE</Label>
                                            <Input type="select" className="form-select-lg rounded-3" value={formData.payment_type} onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}>
                                                <option value="purchase">On Purchase Basis</option>
                                                <option value="amount">Direct Settlement</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        <Card className="border-0 shadow-sm rounded-4">
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4 border-start border-4 border-success ps-3">Payment Amount</h5>
                                <FormGroup className="mb-0">
                                    <Label className="small fw-bold text-success">PAYING AMOUNT *</Label>
                                    <InputGroup size="lg">
                                        <InputGroupText className="bg-white border-end-0 text-success fw-bold">₹</InputGroupText>
                                        <Input
                                            className={`border-start-0 ps-0 fw-bold fs-4 ${errors.amount ? 'is-invalid' : ''}`}
                                            type="number"
                                            onKeyDown={blockInvalidChar}
                                            value={formData.amount}
                                            onChange={(e) => {setFormData({ ...formData, amount: e.target.value }); setErrors({...errors, amount:""})}}
                                            placeholder="0.00"
                                        />
                                    </InputGroup>
                                    {errors.amount && <small className="text-danger mt-1 d-block">{errors.amount}</small>}
                                    {formData.amount > 0 && profitInfo.rate_diff > 0 && (
                                        <div className="mt-2">
                                            <Badge color="info" className="p-2 px-3 rounded-pill">
                                                Reference: {referenceKg} KG
                                            </Badge>
                                        </div>
                                    )}
                                </FormGroup>
                                <FormGroup className="mt-4">
                                    <Label className="small fw-bold opacity-75">REMARKS / NOTES</Label>
                                    <Input type="textarea" className="rounded-3" rows="2" placeholder="Enter payment details..." value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} />
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={5}>
                        <Card className="border-0 shadow-sm rounded-4 sticky-top bg-theme text-white overflow-hidden shadow-lg" style={{ top: '100px' }}>
                            <div className="p-4 bg-dark bg-opacity-25 text-center">
                                <h6 className="opacity-75 text-uppercase small fw-bold mb-1">Available Balance</h6>
                                <h1 className="fw-bold mb-0 text-warning">
                                    ₹ {Math.round(profitInfo.balance)?.toLocaleString()}
                                </h1>
                            </div>
                            <CardBody className="p-4">
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between mb-3 small">
                                        <span className="opacity-75">Calculated Payable:</span>
                                        <span className="fw-bold">₹ {Math.round(profitInfo.total_payable).toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 small">
                                        <span className="opacity-75">Already Settled:</span>
                                        <span className="fw-bold text-info">₹ {Math.round(profitInfo.already_paid)?.toLocaleString()}</span>
                                    </div>
                                    <hr className="my-3 border-white opacity-10" />
                                    <div className="d-flex justify-content-between fs-4 text-warning fw-bold">
                                        <span>Current Payment:</span>
                                        <span>₹ {Number(formData.amount || 0)?.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mt-2 small opacity-75">
                                        <span>Closing Balance:</span>
                                        <span>₹ {Math.max(0, (profitInfo.balance - Number(formData.amount || 0)))?.toLocaleString()}</span>
                                    </div>
                                </div>

                                {formData.invoice_id && profitInfo.balance <= 0 ? (
                                    <Alert color="success" className="bg-success text-white border-0 small py-3 mb-0">
                                        <i className="bx bx-check-double me-2 fs-5"></i>
                                        <b>Completely Settled!</b> All commissions/losses for this invoice are cleared.
                                    </Alert>
                                ) : (
                                    <Button
                                        color="light" block className="py-3 fw-bold text-theme rounded-4 w-100 text-uppercase border-0 shadow"
                                        type="submit"
                                    >
                                        <i className="bx bx-save me-1"></i> Save Record
                                    </Button>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default AgentPaymentCreate;